/**
 * SERVER-SIDE CART REPRICING — the web checkout's answer to "what does this
 * cart cost?", computed from the live menu and never from the cart itself.
 *
 * WHY THIS EXISTS. A web cart is written from the BROWSER: the carts rule lets
 * the owner update the document until it is converted, so `items[].price` and
 * every `modifier.priceDelta` are values the guest can set to anything. The
 * checkout summed them as-is, which made online the one order source that
 * charged a client-supplied number (the till prices with the same rules in its
 * Dart mirror, and a marketplace hands us the provider's price). See
 * docs/superpowers/specs/2026-09-24-order-source-unification-design.md, item A1.
 *
 * WHAT IT DOES. Every line is repriced with the SAME functions the storefront
 * and the assistant use to write a line — `resolveItemPrice` →
 * `resolveCartUnitPrice` for the item (size included), and
 * `resolveModifierPriceOverrides` → `resolveModifierUnitPrice` for each option
 * — so there is no second pricing rule to drift. The returned cart carries the
 * server's numbers; the stored cart's numbers are read only to SELECT between
 * server prices (below), never as a price.
 *
 * THE PATH. A price depends on the ROOT→LEAF group path the guest navigated
 * (group pricing, `SubgroupReference` overrides, parent modifier overrides), but
 * a cart line stores only its LEAF `menuGroupId` (a native app writes nothing
 * else). The storefront's own path is a pruned breadth-first walk over the
 * TIME-filtered menu (`buildOrderingMenuTree`), and this re-pricing has no clock
 * outside scheduled pricing. So it enumerates every structural path to that leaf
 * that the STOREFRONT could have shown — from the menu it opens on (or one its
 * picker offers) and through no section the website hides
 * (`options.storefront`) — prices the line on each, and:
 *   - takes the path whose server price equals what the line claims — the guest
 *     navigated SOME legitimate path, and every candidate is a server price;
 *   - otherwise takes the shortest path (the storefront's own tie-break) and
 *     reports the line as CHANGED.
 * The only freedom a hand-edited cart retains is choosing between prices the
 * storefront itself offers for that group — never inventing one, and never
 * reaching for a menu the website does not offer. Whether the path is OPEN at
 * the instant the order is for is the acceptance validator's question
 * (`orderValidationService`, which walks the same tree with the clock).
 *
 * WHAT IT REFUSES rather than guesses: an item or option that is no longer on
 * the menu — or, with a storefront, not on the menu the storefront offers (under
 * another root, or only in a hidden section: `unknown_item`) — a size-priced line
 * with no (or an unknown) size, a quantity that is not a positive whole number,
 * and a composite pick its parent does not offer.
 * Channel visibility and stock are NOT decided here — the acceptance validator
 * (`orderValidationService`, run with channel `ONLINE_ORDERING`) owns those.
 *
 * OPEN-PRICE items (`isOpenPriceItem`: `pricingStrategy: 'open'` or the
 * `openPrice` flag) are REFUSED (`open_price_not_online`). Owner decision
 * 2026-09-24: they are till only — online, the guest would be naming the price,
 * and keeping `line.price` was exactly "charge the number the browser sent".
 * The channel predicate hides them from every online surface; this is the
 * money-side refusal for a cart that holds one anyway.
 *
 * SCHEDULED MENU PRICING (`options.scheduled`, the tenant's
 * `initiative.scheduled_menu_pricing`): the "any structural path" freedom above
 * is exactly what let a 5:59 PM Happy Hour price through at 7 PM. With it on,
 * each line has ONE legitimate route — the one that prices it at the server's
 * `now` (`resolveOnlineScheduledPaths`, the storefront's own rule) — and a line
 * that disagrees is refused rather than silently re-priced, so the guest sees
 * the new price before paying. See `CartRepricingOptions.scheduled`.
 *
 * Pure: no Firestore; the clock arrives as `options.scheduled.now`.
 */

import type { IComponentChoice } from '../menus/ComponentGroup';
import type { IItem } from '../menus/Item';
import type { IMenuGroup } from '../menus/MenuGroup';
import type { IModifier } from '../menus/Modifier';
import type { IModifierGroup } from '../menus/ModifierGroup';
import {
  modifierOfferedOnItem,
  resolveCartUnitPrice,
  resolveItemPrice,
  resolveModifierPriceOverrides,
  resolveModifierUnitPrice,
  type ItemModifierLinks,
} from '../menus/menuPricingResolver';
import { isOpenPriceItem } from '../menus/channelVisibility';
import { enumerateItemMenuPaths } from '../menus/menuTree';
import {
  resolveOnlineStorefrontScope,
  type OnlineStorefrontScope,
  type WebsiteMenuSelectionConfig,
} from '../menus/websiteMenuSelection';
import {
  describeItemUnavailableNow,
  describeScheduledPriceChange,
  resolveOnlineScheduledPaths,
  scheduledPriceChangeReason,
  type ScheduledPriceChangeReason,
} from '../menus/scheduledMenuOnline';
import { Cart } from './Cart';
import { CartItem, type ICartItem } from './CartItem';
import { CartModifier, cartModifierSelectionTotal } from './CartModifier';
import { CartModifierGroup } from './CartModifierGroup';

/** The live menu a cart is priced against — the four collections, together. */
export interface CartPricingMenu {
  readonly items: readonly IItem[];
  readonly menuGroups: readonly IMenuGroup[];
  readonly modifierGroups: readonly IModifierGroup[];
  readonly modifiers: readonly IModifier[];
}

export interface CartRepricingOptions {
  /**
   * The storefront the cart was built on: the location's `websiteConfig` (its
   * pinned root and its hidden sections). Present — every web checkout — a
   * line is priced only on a path the storefront can show: from the menu it
   * opens on (`selectOrderingMenu`: the pin when that is an online candidate,
   * else the single candidate) or, when it opens on none, from a menu its
   * picker offers; and through no section the website hides. A line with no
   * such path is REFUSED (`unknown_item`) — never priced on a path through
   * another menu, which is how a client that ignored the pin sold from menus
   * the website does not offer. Absent: every structural path, as before.
   */
  readonly storefront?: { readonly websiteConfig?: WebsiteMenuSelectionConfig };
  /**
   * SCHEDULED MENU PRICING (the tenant's `initiative.scheduled_menu_pricing`,
   * read by the caller, fail-closed). Present: every line is priced on the ONE
   * route that prices it at `now` online — `resolveOnlineScheduledPaths`, the
   * storefront's own rule — instead of "any structural path whose price the
   * line claims". A line whose price differs is REFUSED
   * (`scheduled_price_changed`, carrying old/new and why) so the guest sees and
   * confirms the new price; a line no open route reaches is REFUSED
   * (`not_available_now`). Absent: today's rule, unchanged.
   */
  readonly scheduled?: {
    readonly now: Date;
    /** The restaurant's IANA zone. */
    readonly timeZone: string | null | undefined;
  };
}

/** The scheduled-pricing context of a re-price (`CartRepricingOptions.scheduled`). */
export type ScheduledCartPricing = NonNullable<CartRepricingOptions['scheduled']>;

export type CartLineRefusalKind =
  | 'unknown_item'
  | 'size_required'
  | 'unknown_size'
  | 'open_price_not_online'
  | 'unknown_modifier_group'
  | 'unknown_modifier'
  | 'invalid_quantity'
  | 'composite_parent_missing'
  | 'composite_choice_not_offered'
  /** Scheduled pricing only: no open route reaches the item now (a Happy-Hour-only drink at 7 PM). */
  | 'not_available_now'
  /** Scheduled pricing only: the line's price is not the price of the route that prices it now. */
  | 'scheduled_price_changed';

/** A line that cannot be priced from the live menu. */
export interface CartLineRefusal {
  readonly kind: CartLineRefusalKind;
  readonly cartItemId: string;
  readonly menuItemId: number;
  /** The line's name as the guest saw it. */
  readonly name: string;
  /** The guest-facing sentence, for the scheduled kinds ("Happy Hour ended — Tito's is now $9.00"). */
  readonly message?: string;
  /** `scheduled_price_changed`: item + options, one unit, as the cart holds it. */
  readonly clientUnitPrice?: number;
  /** `scheduled_price_changed`: item + options, one unit, on the route that prices it now. */
  readonly serverUnitPrice?: number;
  /** `scheduled_price_changed`: why. */
  readonly reason?: ScheduledPriceChangeReason;
  /** `scheduled_price_changed`: the route that prices it now. */
  readonly menuGroupPath?: readonly number[];
  /**
   * `scheduled_price_changed`: the line exactly as the server would price it
   * now (item price, option prices, route, leaf). The checkout writes THIS back
   * on the guest's confirmation — the server's answer is authoritative, so a
   * client whose clock disagrees cannot loop the guest through refusals.
   */
  readonly repricedLine?: ICartItem;
}

/** A line whose stored price is not the server's. Unit = item + options. */
export interface RepricedCartLine {
  readonly cartItemId: string;
  readonly name: string;
  readonly clientUnitPrice: number;
  readonly serverUnitPrice: number;
}

export type CartRepricing =
  | {
      readonly ok: true;
      /** The cart with every price (and tax assignment) taken from the menu. */
      readonly cart: Cart;
      /** Lines whose stored price or tax assignment differed from the server's. */
      readonly changedLines: RepricedCartLine[];
      /**
       * Cart lines whose menu item is "Exclude from discounts", by
       * `cartItemId`. Read from the live menu, never from the cart, so a guest
       * cannot clear it; a promo code is sized without these lines.
       */
      readonly discountExcludedCartItemIds: readonly string[];
    }
  | { readonly ok: false; readonly refusals: CartLineRefusal[] };

/** Half a cent: below this two money amounts are the same amount. */
const SAME_MONEY = 0.005;
const sameMoney = (a: number, b: number): boolean => Math.abs(a - b) < SAME_MONEY;

const isPositiveWhole = (n: unknown): n is number =>
  typeof n === 'number' && Number.isInteger(n) && n >= 1;

/**
 * The modifier as the storefront prices it: the library record with the
 * group's reference overlaid. `modifierGroups.ts` hydrates exactly this way —
 * `price` becomes the reference's `priceOverride` when one is set, and
 * `priceOverride` becomes the reference's (absent when the reference has none).
 */
function modifierAsOffered(modifier: IModifier, group: IModifierGroup): IModifier {
  const ref = (group.modifiers ?? []).find((r) => r.modifierId === modifier.modifierId);
  return {
    ...modifier,
    ...(ref?.priceOverride !== undefined ? { price: ref.priceOverride } : {}),
    priceOverride: ref?.priceOverride,
  } as IModifier;
}

/** Distinct paths, shortest first (the storefront's own tie-break). */
function uniqueShortestFirst(paths: number[][]): number[][] {
  const seen = new Set<string>();
  const unique = paths.filter((p) => {
    const key = p.join('>');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  unique.sort((a, b) => a.length - b.length);
  return unique;
}

/** Structural ROOT→LEAF paths to the item, ending at the line's leaf when it names one. */
function itemPathsToLeaf(
  menuItemId: number,
  leafGroupId: number | undefined,
  menuGroups: readonly IMenuGroup[],
  walk?: Parameters<typeof enumerateItemMenuPaths>[2],
): number[][] {
  const paths = enumerateItemMenuPaths(menuGroups, menuItemId, walk).map((p) => [...p.menuGroupPath]);
  return leafGroupId != null ? paths.filter((p) => p[p.length - 1] === leafGroupId) : paths;
}

/**
 * The paths the STOREFRONT could have priced a line on: walked from its own
 * roots (never `findMenuRoots`, which an `isMenu` flag can steer away from the
 * menu the storefront opened on) and entering no hidden section. Empty when the
 * storefront cannot reach the line's group — the caller refuses it.
 */
function storefrontPaths(
  menuItemId: number,
  leafGroupId: number | undefined,
  menuGroups: readonly IMenuGroup[],
  scope: OnlineStorefrontScope,
): number[][] {
  return uniqueShortestFirst(
    itemPathsToLeaf(menuItemId, leafGroupId, menuGroups, {
      rootIds: scope.rootIds,
      enterGroup: (group) => !scope.hiddenSectionIds.has(group.menuGroupId as number),
    }),
  );
}

/**
 * ROOT→LEAF paths a line may have been priced on with no storefront to hold it
 * to, shortest first — and a COMPOSITE pick's paths, which only select its
 * option overrides (its price is its parent's choice): the storefront's paths
 * when it has any, else every structural path, else the leaf alone (or — when
 * the line names no group — the item itself, as the storefront does for an item
 * it could not place).
 */
function structuralPaths(
  menuItemId: number,
  leafGroupId: number | undefined,
  menuGroups: readonly IMenuGroup[],
  scope: OnlineStorefrontScope | null,
): Array<number[] | undefined> {
  const scoped = scope ? storefrontPaths(menuItemId, leafGroupId, menuGroups, scope) : [];
  if (scoped.length > 0) return scoped;
  const unique = uniqueShortestFirst(itemPathsToLeaf(menuItemId, leafGroupId, menuGroups));
  if (unique.length > 0) return unique;
  return [leafGroupId != null ? [leafGroupId] : undefined];
}

interface PricedOption {
  readonly itemPrice: number;
  readonly modifierGroups: CartModifierGroup[];
}

type OptionResult = { ok: true; option: PricedOption } | { ok: false; kind: CartLineRefusalKind };

function priceModifiers(
  line: CartItem,
  path: number[] | undefined,
  menu: CartPricingMenu,
  /** The line's own item — its reference to a group prices that group's options on it. */
  item: ItemModifierLinks | null,
): { ok: true; groups: CartModifierGroup[] } | { ok: false; kind: CartLineRefusalKind } {
  const menuGroups = menu.menuGroups as IMenuGroup[];
  const groups: CartModifierGroup[] = [];
  for (const cartGroup of line.modifierGroups) {
    const group = menu.modifierGroups.find((g) => g.modifierGroupId === cartGroup.modifierGroupId);
    if (!group) return { ok: false, kind: 'unknown_modifier_group' };
    const parentOverrides = resolveModifierPriceOverrides(group.modifierGroupId as number, path, menuGroups, item);
    const modifiers: CartModifier[] = [];
    for (const cartMod of cartGroup.modifiers) {
      if (!isPositiveWhole(cartMod.quantity)) return { ok: false, kind: 'invalid_quantity' };
      const modifier = menu.modifiers.find((m) => m.modifierId === cartMod.modifierId);
      if (!modifier) return { ok: false, kind: 'unknown_modifier' };
      // A `pricedByItem` option the item does not price is not on its menu (a
      // Glass of a wine sold by the bottle only) — never charged at the
      // option's own price.
      if (!modifierOfferedOnItem(group, cartMod.modifierId, item)) return { ok: false, kind: 'unknown_modifier' };
      modifiers.push(
        new CartModifier({
          modifierId: cartMod.modifierId,
          modifierGuid: cartMod.modifierGuid,
          name: cartMod.name,
          priceDelta: resolveModifierUnitPrice(modifierAsOffered(modifier, group), group, parentOverrides),
          quantity: cartMod.quantity,
        }),
      );
    }
    groups.push(
      new CartModifierGroup({
        ...cartGroup.toJSON(),
        modifiers,
      } as ConstructorParameters<typeof CartModifierGroup>[0]),
    );
  }
  return { ok: true, groups };
}

/** The item's unit price on one path (the size the line chose included). */
function priceItemOnPath(
  line: CartItem,
  item: IItem,
  path: number[] | undefined,
  menu: CartPricingMenu,
): { ok: true; price: number } | { ok: false; kind: CartLineRefusalKind } {
  const leaf = path?.[path.length - 1] ?? line.menuGroupId;
  const resolved = resolveItemPrice(item, path, leaf, menu.menuGroups as IMenuGroup[]);
  if (resolved.kind === 'sizes') {
    const sizeName = line.selectedSize?.sizeName;
    if (!sizeName) return { ok: false, kind: 'size_required' };
    const size = resolved.sizes.find((s) => s.name === sizeName);
    if (!size) return { ok: false, kind: 'unknown_size' };
    return { ok: true, price: resolveCartUnitPrice(item, resolved, { name: size.name, price: size.price }) };
  }
  // A group price can land an item on 'open' too; never keep the guest's figure.
  if (resolved.kind === 'open') return { ok: false, kind: 'open_price_not_online' };
  return { ok: true, price: resolveCartUnitPrice(item, resolved) };
}

/** Composite child: its price is the parent's offered upcharge, never its own. */
function childChoices(line: CartItem, cart: Cart, menu: CartPricingMenu): IComponentChoice[] | CartLineRefusalKind {
  const parentLine = cart.items.find((l) => l.cartItemId === line.bundleParentCartItemId);
  const parentItem = parentLine && menu.items.find((i) => i.itemId === parentLine.menuItemId);
  if (!parentItem) return 'composite_parent_missing';
  const choices = (parentItem.componentGroups ?? []).flatMap((g) =>
    g.choices.filter((c) => c.itemId === line.menuItemId),
  );
  return choices.length > 0 ? choices : 'composite_choice_not_offered';
}

function lineUnit(option: PricedOption): number {
  return (
    option.itemPrice +
    option.modifierGroups.reduce(
      (sum, g) => sum + g.modifiers.reduce((s, m) => s + cartModifierSelectionTotal(m), 0),
      0,
    )
  );
}

/**
 * THE comparison of a cart line against a price the menu offers it: the item
 * price AND every option's price, each to the half cent. The server
 * (`repriceCart`) and the storefront's checkout-start check
 * (`findCheckoutPriceChanges`) both call this, so "the price changed" means the
 * same thing on both sides of checkout — a storefront that compared only the
 * unit total could pass a line the server refuses for a swapped option price.
 */
export function cartLinePricesMatch(
  priced: { readonly itemPrice: number; readonly modifierGroups: readonly CartModifierGroup[] },
  line: Pick<CartItem, 'price' | 'modifierGroups'>,
): boolean {
  if (!sameMoney(priced.itemPrice, line.price)) return false;
  if (priced.modifierGroups.length !== line.modifierGroups.length) return false;
  return priced.modifierGroups.every((g, gi) => {
    const stored = line.modifierGroups[gi]!;
    return (
      g.modifiers.length === stored.modifiers.length &&
      g.modifiers.every((m, mi) => sameMoney(m.priceDelta, stored.modifiers[mi]!.priceDelta))
    );
  });
}

function matchesLine(option: PricedOption, line: CartItem): boolean {
  return cartLinePricesMatch(option, line);
}

/**
 * The roots a line's storefront walked from, in the order to try them. A
 * storefront that opens on ONE menu (the pin, or the single candidate) was
 * rooted there whatever the browser-written line records. A picker storefront
 * was rooted at the menu the guest picked — the line's recorded root, when it
 * is one the picker offers; a line that records none (a native app) may have
 * come from any of them. No storefront: the recorded root, else any root.
 */
function lineRootIds(line: CartItem, scope: OnlineStorefrontScope | null): Array<number | undefined> {
  const recorded = line.menuGroupPath?.[0];
  if (!scope) return [recorded];
  if (scope.rootIds.length === 1) return [scope.rootIds[0]];
  if (recorded != null && scope.rootIds.includes(recorded)) return [recorded];
  return [...scope.rootIds];
}

/**
 * The scheduled route for each line, from one walk per storefront root (lines
 * from a multi-menu storefront may carry different roots).
 */
function scheduledPathLookup(
  cart: Cart,
  menu: CartPricingMenu,
  options: CartRepricingOptions,
  scope: OnlineStorefrontScope | null,
): (line: CartItem) => number[] | undefined {
  const scheduled = options.scheduled!;
  const byRoot = new Map<string, Map<number, number[]>>();
  const pathsFrom = (rootId: number | undefined): Map<number, number[]> => {
    const key = String(rootId ?? 'any');
    let paths = byRoot.get(key);
    if (!paths) {
      paths = resolveOnlineScheduledPaths({
        ...(rootId != null ? { rootId } : {}),
        menuGroups: menu.menuGroups,
        ...(scope ? { hiddenSectionIds: scope.hiddenSectionIds } : {}),
        orderType: cart.orderType ?? 'pickup',
        now: scheduled.now,
        timeZone: scheduled.timeZone,
        itemIds: cart.items.map((l) => l.menuItemId),
      });
      byRoot.set(key, paths);
    }
    return paths;
  };
  return (line) => {
    for (const rootId of lineRootIds(line, scope)) {
      const path = pathsFrom(rootId).get(line.menuItemId);
      if (path) return path;
    }
    return undefined;
  };
}

/**
 * Reprice every line of `cart` from `menu`. See the file header for the rules.
 */
export function repriceCart(
  cart: Cart,
  menu: CartPricingMenu,
  options: CartRepricingOptions = {},
): CartRepricing {
  const refusals: CartLineRefusal[] = [];
  const changedLines: RepricedCartLine[] = [];
  const discountExcludedCartItemIds: string[] = [];
  const lines: CartItem[] = [];
  const scope = options.storefront
    ? resolveOnlineStorefrontScope(menu.menuGroups, options.storefront.websiteConfig)
    : null;
  const scheduledPathFor = options.scheduled ? scheduledPathLookup(cart, menu, options, scope) : null;

  for (const line of cart.items) {
    const refuse = (kind: CartLineRefusalKind, extra: Partial<CartLineRefusal> = {}): void => {
      refusals.push({ kind, cartItemId: line.cartItemId, menuItemId: line.menuItemId, name: line.name, ...extra });
    };

    if (!isPositiveWhole(line.quantity)) {
      refuse('invalid_quantity');
      continue;
    }
    const item = menu.items.find((i) => i.itemId === line.menuItemId);
    if (!item) {
      refuse('unknown_item');
      continue;
    }
    if (isOpenPriceItem(item)) {
      refuse('open_price_not_online');
      continue;
    }

    // Scheduled pricing: a priced line has exactly ONE legitimate route — the
    // one that prices it now. A composite child is priced by its parent's
    // choice, not a route, so it keeps the structural candidates.
    let scheduledPath: number[] | undefined;
    if (scheduledPathFor && !line.bundleParentCartItemId) {
      scheduledPath = scheduledPathFor(line);
      if (!scheduledPath) {
        const roots = lineRootIds(line, scope);
        refuse('not_available_now', {
          message: describeItemUnavailableNow(
            line.name,
            line.menuItemId,
            menu.menuGroups,
            roots.length === 1 ? roots[0] : undefined,
          ),
        });
        continue;
      }
    }
    let paths: Array<number[] | undefined>;
    if (scheduledPath) {
      paths = [scheduledPath];
    } else if (scope && !line.bundleParentCartItemId) {
      // A line the storefront cannot reach is not on the online menu: refused,
      // never priced on a path through another menu or a hidden section.
      paths = storefrontPaths(line.menuItemId, line.menuGroupId, menu.menuGroups, scope);
      if (paths.length === 0) {
        refuse('unknown_item');
        continue;
      }
    } else {
      paths = structuralPaths(line.menuItemId, line.menuGroupId, menu.menuGroups, scope);
    }

    // Every server price this line could legitimately carry.
    const results: OptionResult[] = [];
    if (line.bundleParentCartItemId) {
      const choices = childChoices(line, cart, menu);
      if (typeof choices === 'string') {
        refuse(choices);
        continue;
      }
      for (const choice of choices) {
        for (const path of paths) {
          const mods = priceModifiers(line, path, menu, item ?? null);
          results.push(
            mods.ok
              ? { ok: true, option: { itemPrice: choice.priceDelta ?? 0, modifierGroups: mods.groups } }
              : { ok: false, kind: mods.kind },
          );
        }
      }
    } else {
      for (const path of paths) {
        const priced = priceItemOnPath(line, item, path, menu);
        if (!priced.ok) {
          results.push({ ok: false, kind: priced.kind });
          continue;
        }
        const mods = priceModifiers(line, path, menu, item ?? null);
        results.push(
          mods.ok
            ? { ok: true, option: { itemPrice: priced.price, modifierGroups: mods.groups } }
            : { ok: false, kind: mods.kind },
        );
      }
    }

    const priceable = results.flatMap((r) => (r.ok ? [r.option] : []));
    if (priceable.length === 0) {
      const firstFailure = results.find((r): r is { ok: false; kind: CartLineRefusalKind } => !r.ok);
      refuse(firstFailure?.kind ?? 'unknown_item');
      continue;
    }

    const matched = priceable.find((o) => matchesLine(o, line));
    const chosen = matched ?? priceable[0];
    if (scheduledPath && !matched) {
      // Never charge a price the guest was not shown, and never the old Happy
      // Hour price after the window: refuse, saying what it costs now and why.
      const reason = scheduledPriceChangeReason(line.menuGroupPath, scheduledPath, menu.menuGroups);
      const serverUnitPrice = lineUnit(chosen);
      refuse('scheduled_price_changed', {
        clientUnitPrice: line.unitPrice,
        serverUnitPrice,
        reason,
        menuGroupPath: scheduledPath,
        repricedLine: new CartItem({
          ...line.toJSON(),
          price: chosen.itemPrice,
          modifierGroups: chosen.modifierGroups,
          menuGroupPath: scheduledPath,
          menuGroupId: scheduledPath[scheduledPath.length - 1],
        } as ConstructorParameters<typeof CartItem>[0]).toJSON(),
        message: describeScheduledPriceChange({ name: line.name, newUnitPrice: serverUnitPrice, reason }),
      });
      continue;
    }
    const taxRateIds = item.taxRateIds ?? [];
    const taxInclusion = item.taxInclusion ?? false;
    const taxChanged =
      taxInclusion !== line.taxInclusion ||
      taxRateIds.length !== line.taxRateIds.length ||
      taxRateIds.some((id, i) => id !== line.taxRateIds[i]);

    if (!matched || taxChanged) {
      changedLines.push({
        cartItemId: line.cartItemId,
        name: line.name,
        clientUnitPrice: line.unitPrice,
        serverUnitPrice: lineUnit(chosen),
      });
    }

    if (item.excludeFromDiscounts === true) discountExcludedCartItemIds.push(line.cartItemId);

    lines.push(
      new CartItem({
        ...line.toJSON(),
        price: chosen.itemPrice,
        modifierGroups: chosen.modifierGroups,
        taxRateIds: [...taxRateIds],
        taxInclusion,
      }),
    );
  }

  if (refusals.length > 0) return { ok: false, refusals };
  return {
    ok: true,
    cart: new Cart({ ...cart.toJSON(), items: lines } as ConstructorParameters<typeof Cart>[0]),
    changedLines,
    discountExcludedCartItemIds,
  };
}

/**
 * The cart with the server's scheduled-pricing refusals ACCEPTED: each
 * `scheduled_price_changed` line replaced by its `repricedLine`, each
 * `not_available_now` line removed together with the composite picks under it.
 * Lines not named stay as they are. What the checkout writes when the guest
 * confirms the server's old → new notice; re-pricing the result at the same
 * instant refuses nothing (pinned in `cartRepricingScheduled.test.ts`).
 */
export function applyServerRepricing(cart: Cart, refusals: readonly CartLineRefusal[]): Cart {
  const replace = new Map(
    refusals
      .filter((r) => r.kind === 'scheduled_price_changed' && r.repricedLine != null)
      .map((r) => [r.cartItemId, r.repricedLine!] as const),
  );
  const remove = new Set(refusals.filter((r) => r.kind === 'not_available_now').map((r) => r.cartItemId));
  const items = cart.items
    .filter((l) => !remove.has(l.cartItemId) && !(l.bundleParentCartItemId && remove.has(l.bundleParentCartItemId)))
    .map((l) => {
      const next = replace.get(l.cartItemId);
      return next ? CartItem.fromJSON(next as unknown as Record<string, unknown>) : l;
    });
  return new Cart({ ...cart.toJSON(), items } as ConstructorParameters<typeof Cart>[0]);
}
